import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import * as MaintenanceScheduleActions from '../actions/maintenance-schedule.actions';
import {
  MaintenanceAlertApiService,
  MaintenanceSchedule,
  MaintenanceScheduleApiService,
  NotificationService,
} from '@fc-core';
import { MatDialog } from '@angular/material/dialog';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class MaintenanceScheduleEffects {
  loadMaintenanceSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceScheduleActions.loadMaintenanceSchedules),
      switchMap(({ vehicleId }) => {
        return this.maintenanceScheduleApi.loadAll(vehicleId).pipe(
          map((maintenanceSchedules) => {
            return MaintenanceScheduleActions.loadMaintenanceSchedulesSuccess({
              schedules: maintenanceSchedules,
            });
          }),
          catchError((err) =>
            of(MaintenanceScheduleActions.maintenanceSchedulesError({ err })),
          ),
        );
      }),
    ),
  );
  loadTooltipMaintenanceSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceScheduleActions.loadTooltipMaintenanceSchedules),
      switchMap(({ vehicleId }) => {
        return this.maintenanceScheduleApi.loadAll(vehicleId).pipe(
          map((maintenanceSchedules) => {
            return MaintenanceScheduleActions.loadTooltipMaintenanceSchedulesSuccess(
              {
                schedules: maintenanceSchedules,
              },
            );
          }),
          catchError((err) =>
            of(MaintenanceScheduleActions.maintenanceSchedulesError({ err })),
          ),
        );
      }),
    ),
  );
  addSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceScheduleActions.addMaintenanceSchedule),
      switchMap(({ maintenanceSchedule }) => {
        return this.maintenanceScheduleApi.create(maintenanceSchedule).pipe(
          map((schedule) => {
            this.dialog.closeAll();
            this.notifications.showSuccess(
              'Maintenance schedule successfully added',
            );
            return MaintenanceScheduleActions.addMaintenanceScheduleSuccess({
              maintenanceSchedule: schedule,
            });
          }),
          catchError((err) => {
            this.notifications.showError(getErrorMessage(err));
            return of(
              MaintenanceScheduleActions.maintenanceSchedulesError({ err }),
            );
          }),
        );
      }),
    ),
  );
  deleteSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceScheduleActions.deleteMaintenanceSchedule),
      switchMap(({ maintenanceSchedule }) => {
        return this.maintenanceScheduleApi.delete(maintenanceSchedule).pipe(
          map((schedule: MaintenanceSchedule) => {
            this.notifications.showSuccess(
              'Maintenance schedule successfully deleted',
            );
            return MaintenanceScheduleActions.deleteMaintenanceScheduleSuccess({
              maintenanceSchedule: schedule,
            });
          }),
          catchError((err) =>
            of(MaintenanceScheduleActions.maintenanceSchedulesError({ err })),
          ),
        );
      }),
    ),
  );
  updateSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceScheduleActions.updateMaintenanceSchedule),
      switchMap(({ maintenanceSchedule }) => {
        return this.maintenanceScheduleApi
          .maintenanceUpdate(maintenanceSchedule)
          .pipe(
            map((schedule: MaintenanceSchedule) => {
              this.dialog.closeAll();
              this.notifications.showSuccess(
                'Maintenance schedule successfully updated',
              );
              return MaintenanceScheduleActions.updateMaintenanceScheduleSuccess(
                { maintenanceSchedule: schedule },
              );
            }),
            catchError((err) =>
              of(MaintenanceScheduleActions.maintenanceSchedulesError({ err })),
            ),
          );
      }),
    ),
  );
  closeSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceScheduleActions.closeMaintenanceSchedule),
      switchMap(({ schedule, alert, closePayload, attachments }) =>
        this.maintenanceAlertApiService
          .closeAlert(schedule, alert, closePayload)
          .pipe(
            switchMap(() => {
              if (attachments.length) {
                return this.maintenanceAlertApiService
                  .addMaintenanceAttachments(attachments, alert)
                  .pipe(switchMap(() => this.finalClosure(schedule)));
              }
              return this.finalClosure(schedule);
            }),
            catchError((err) => {
              return of(
                MaintenanceScheduleActions.maintenanceSchedulesError({ err }),
              );
            }),
          ),
      ),
    ),
  );
  loadAllAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceScheduleActions.loadAllMaintenanceScheduleAlerts),
      switchMap(({ schedule, options }) =>
        this.maintenanceAlertApiService.loadAll(schedule, options).pipe(
          map((alertList) =>
            MaintenanceScheduleActions.loadAllMaintenanceScheduleAlertsSuccess({
              alertList,
            }),
          ),
          catchError((err) =>
            of(MaintenanceScheduleActions.maintenanceSchedulesError(err)),
          ),
        ),
      ),
    ),
  );
  updateAlert$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MaintenanceScheduleActions.updateMaintenanceScheduleAlert),
      switchMap(({ payload }) => {
        return this.maintenanceAlertApiService.updateAlert(payload).pipe(
          map((updatedAlert) =>
            MaintenanceScheduleActions.updateMaintenanceScheduleAlertSuccess({
              updatedAlert,
            }),
          ),
          catchError((err) =>
            of(MaintenanceScheduleActions.maintenanceSchedulesError(err)),
          ),
        );
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private maintenanceScheduleApi: MaintenanceScheduleApiService,
    private dialog: MatDialog,
    private maintenanceAlertApiService: MaintenanceAlertApiService,
    private notifications: NotificationService,
  ) {}

  finalClosure(schedule: MaintenanceSchedule) {
    return this.maintenanceScheduleApi.loadAll(schedule.vehicle).pipe(
      switchMap((maintenanceSchedules: MaintenanceSchedule[]) => {
        this.dialog.closeAll();
        return [
          MaintenanceScheduleActions.loadMaintenanceSchedulesSuccess({
            schedules: maintenanceSchedules,
          }),
          MaintenanceScheduleActions.closeMaintenanceScheduleSuccess(),
        ];
      }),
    );
  }
}
